import { makeAutoObservable, runInAction } from "mobx";
import {
    SupplierStats,
    PageStatsResponse,
    MediaStatsResponse,
    DeviceStatsResponse,
    FlightStatsResponse,
} from "../types";
import { apiService } from "../services/api";

type StatEndpoint = "page" | "media" | "device" | "flight";

class StatsStore {
    stats: SupplierStats | null = null;
    pageStats: PageStatsResponse | null = null;
    mediaStats: MediaStatsResponse | null = null;
    deviceStats: DeviceStatsResponse | null = null;
    flightStats: FlightStatsResponse | null = null;
    loading = false;
    error: string | null = null;
    dateRange = {
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
    };
    filters = {
        clientId: null as number | null,
    };
    lastPageStatsKey: string | null = null;
    lastMediaStatsKey: string | null = null;
    lastMediaSummaryKey: string | null = null;

    private pendingRequests = 0;
    private endpointLoading: Record<StatEndpoint, boolean> = {
        page: false,
        media: false,
        device: false,
        flight: false,
    };
    private endpointError: Record<StatEndpoint, string | null> = {
        page: null,
        media: null,
        device: null,
        flight: null,
    };
    private requestVersion: Record<StatEndpoint, number> = {
        page: 0,
        media: 0,
        device: 0,
        flight: 0,
    };
    private inFlightKey: Partial<Record<StatEndpoint, string>> = {};
    private controllers: Partial<Record<StatEndpoint, AbortController>> = {};

    constructor() {
        makeAutoObservable(this);
    }

    setError = (message: string | null) => {
        this.error = message;
    };

    private unwrapApiEnvelope<T = any>(value: any): T {
        if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            "data" in value &&
            (Object.prototype.hasOwnProperty.call(value, "success") ||
                Object.prototype.hasOwnProperty.call(value, "code") ||
                Object.prototype.hasOwnProperty.call(value, "message"))
        ) {
            return value.data as T;
        }
        return value as T;
    }

    private normalizeTablePayload(response: any) {
        const unwrappedPayload = this.unwrapApiEnvelope<any>(response);
        const payload =
            Array.isArray(unwrappedPayload) &&
            response &&
            typeof response === "object" &&
            !Array.isArray(response) &&
            response.data === unwrappedPayload
                ? response
                : unwrappedPayload;
        if (Array.isArray(payload)) {
            return {
                rows: payload,
                total: payload.length,
                totalViewCount: 0,
                totalUniqueVisitors: undefined,
                totalUniqueViewers: undefined,
            };
        }

        const rows = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.rows)
            ? payload.rows
            : Array.isArray(payload?.items)
            ? payload.items
            : [];

        const totalViewCount = Number(payload?.totalViewCount ?? payload?.totalViews ?? 0);
        const totalUniqueVisitors =
            payload?.totalUniqueVisitors === undefined ? undefined : Number(payload.totalUniqueVisitors);
        const totalUniqueViewers =
            payload?.totalUniqueViewers === undefined ? totalUniqueVisitors : Number(payload.totalUniqueViewers);
        const total = Number(payload?.total ?? payload?.count ?? rows.length ?? 0);

        return { rows, total, totalViewCount, totalUniqueVisitors, totalUniqueViewers };
    }

    private beginRequest = () => {
        this.pendingRequests += 1;
        this.loading = true;
    };

    private endRequest = () => {
        this.pendingRequests = Math.max(0, this.pendingRequests - 1);
        this.loading = this.pendingRequests > 0;
    };

    private isCancelError = (error: any): boolean => {
        return error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
    };

    private prepareRequest = (endpoint: StatEndpoint, requestKey: string) => {
        if (this.endpointLoading[endpoint] && this.inFlightKey[endpoint] === requestKey) {
            return null;
        }

        this.controllers[endpoint]?.abort();

        const controller = new AbortController();
        this.controllers[endpoint] = controller;
        this.endpointLoading[endpoint] = true;
        this.endpointError[endpoint] = null;
        this.inFlightKey[endpoint] = requestKey;
        this.requestVersion[endpoint] += 1;

        this.beginRequest();

        return {
            controller,
            requestVersion: this.requestVersion[endpoint],
        };
    };

    private finishRequest = (endpoint: StatEndpoint, requestVersion: number) => {
        if (this.requestVersion[endpoint] === requestVersion) {
            this.endpointLoading[endpoint] = false;
            this.inFlightKey[endpoint] = undefined;
        }
        this.endRequest();
    };

    private isLatestRequest = (endpoint: StatEndpoint, requestVersion: number): boolean => {
        return this.requestVersion[endpoint] === requestVersion;
    };

    fetchStats = async (supplierId: number, startDate: Date, endDate: Date, clientId?: number | null): Promise<void> => {
        const requestKey = this.buildStatsKey(clientId, startDate, endDate, supplierId);
        const request = this.prepareRequest("media", `legacy:${requestKey}`);
        if (!request) {
            return;
        }

        runInAction(() => {
            this.error = null;
        });

        try {
            const stats = await apiService.getSupplierStats(supplierId, startDate, endDate, clientId);
            if (!this.isLatestRequest("media", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                this.stats = stats;
            });
        } catch (error: any) {
            if (this.isCancelError(error) || !this.isLatestRequest("media", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                this.error = error.response?.data?.message || "Ошибка загрузки статистики";
            });
        } finally {
            runInAction(() => {
                this.finishRequest("media", request.requestVersion);
            });
        }
    };

    setDateRange = (startDate: Date, endDate: Date): void => {
        this.dateRange = { startDate, endDate };
    };

    setClientFilter = (clientId: number | null): void => {
        this.filters.clientId = clientId;
    };

    clearFilters = (): void => {
        this.filters.clientId = null;
    };

    private buildStatsKey = (
        clientId: number | null | undefined,
        startDate: Date,
        endDate: Date,
        supplierId?: number,
    ): string => {
        return `${supplierId || "all"}|${clientId || "all"}|${startDate.toISOString().split("T")[0]}|${endDate.toISOString().split("T")[0]}`;
    };

    // Обновление только общей статистики (страницы + медиа)
    refreshAllData = async (supplierId: number): Promise<void> => {
        const { startDate, endDate } = this.dateRange;
        const { clientId } = this.filters;

        await this.fetchPageStats(clientId, startDate, endDate, supplierId);
        await this.fetchMediaStats(clientId, startDate, endDate, supplierId, true);
    };

    clearError = (): void => {
        this.error = null;
    };

    get isFlightLoading() {
        return this.endpointLoading.flight;
    }

    get flightError() {
        return this.endpointError.flight;
    }

    get chartData() {
        if (!this.mediaStats?.data || !Array.isArray(this.mediaStats.data)) return [];

        const seriesAggregate = new Map<string, { name: string; views: number; fullName: string }>();

        for (const stat of this.mediaStats.data) {
            let seriesKey: string;
            let displayName: string;

            if (stat.grandparentMediaName) {
                seriesKey = stat.grandparentMediaName.toLowerCase();
                displayName = stat.grandparentMediaName;
            } else if (stat.parentMediaName) {
                seriesKey = stat.parentMediaName.toLowerCase();
                displayName = stat.parentMediaName;
            } else {
                seriesKey = (stat.mediaTitle || stat.actualMediaName || "Без названия").toLowerCase();
                displayName = stat.mediaTitle || stat.actualMediaName || "Без названия";
            }

            const entry = seriesAggregate.get(seriesKey);
            const views = parseInt(stat.viewCount || "0");

            if (entry) {
                entry.views += views;
            } else {
                seriesAggregate.set(seriesKey, {
                    name: displayName,
                    views,
                    fullName: displayName,
                });
            }
        }

        const top = Array.from(seriesAggregate.values())
            .sort((a, b) => b.views - a.views)
            .slice(0, 10);

        return top.map((item) => ({
            name: item.name.length > 40 ? item.name.substring(0, 40) + "..." : item.name,
            views: item.views,
            fullName: item.fullName,
        }));
    }

    get allSeriesData() {
        if (!this.mediaStats?.data) return [];

        const seriesAggregate = new Map<string, { name: string; views: number }>();

        const seriesRegexes: RegExp[] = [
            /^(.+),\s*\d+\s+[Сс]ерия/i,
            /(.*?),\s*\d+\s*сезон[^,]*/i,
            /(.*?)\s*\d+\s*сезон[^,]*/i,
            /(.*?)\s+серия\s+\d+[^,]*/i,
            /(.*?)\s*-\s*серия\s*\d+[^,]*/i,
            /(.*?),\s*эпизод\s*\d+[^,]*/i,
            /(.*?)\s*эпизод\s*\d+[^,]*/i,
            /(.*?),\s*часть\s*\d+[^,]*/i,
            /(.*?)\s*часть\s*\d+[^,]*/i,
            /(.*?),\s*сезон\s*\d+[^,]*/i,
            /(.*?)\s*сезон\s*\d+[^,]*/i,
            /(.*?)\s+\d+$/i,
            /(.*?)\s*-\s*\d+$/i,
            /(.*?)\s+[IVX]+$/i,
            /(.*?)\s+[A-Z]$/i,
            /(.*?)\.\d+$/i,
            /(.*?)_\d+$/i,
            /(.*?)\s*\(\d+\)$/i,
            /(.*?)\s*\[\d+\]$/i,
            /(.*?):\s*\d+$/i,
            /(.*?)\s*—\s*\d+$/i,
            /^(.+?)\s+из\s+\d+$/i,
            /^(.+?)\s+часть\s+\d+$/i,
            /^(.+?)\s+том\s+\d+$/i,
            /^(.+?)\s+книга\s+\d+$/i,
            /^(.+?)\s+глава\s+\d+$/i,
            /^(.+?)\s+выпуск\s+\d+$/i,
        ];

        for (const media of this.mediaStats.data) {
            const original = media.mediaTitle || "";
            let seriesKey: string | null = null;

            if (original) {
                for (const rx of seriesRegexes) {
                    const match = original.match(rx);
                    if (match && match[1]) {
                        seriesKey = match[1].trim();
                        break;
                    }
                }
            }

            const key = (seriesKey || original).toLowerCase();
            const entry = seriesAggregate.get(key);
            const views = parseInt(media.viewCount || "0");

            if (entry) {
                entry.views += views;
            } else {
                seriesAggregate.set(key, {
                    name: seriesKey ? seriesKey : original,
                    views,
                });
            }
        }

        return Array.from(seriesAggregate.values()).sort((a, b) => b.views - a.views);
    }

    get totalViews() {
        return this.stats?.summary.totalViews || 0;
    }

    get topMedia() {
        if (!this.stats?.stats) return [];
        return this.stats.stats.slice(0, 5);
    }

    fetchPageStats = async (
        clientId: number | null | undefined,
        startDate: Date,
        endDate: Date,
        supplierId?: number,
    ) => {
        const requestKey = this.buildStatsKey(clientId, startDate, endDate, supplierId);

        if (this.lastPageStatsKey === requestKey && this.pageStats) {
            return;
        }

        const request = this.prepareRequest("page", requestKey);
        if (!request) {
            return;
        }

        runInAction(() => {
            this.error = null;
        });

        try {
            const response = await apiService.getPagesStats(
                startDate.toISOString().split("T")[0],
                endDate.toISOString().split("T")[0],
                clientId || undefined,
                supplierId,
                { signal: request.controller.signal },
            );

            if (!this.isLatestRequest("page", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                const normalized = this.normalizeTablePayload(response);
                this.pageStats = {
                    data: normalized.rows,
                    total: normalized.total || normalized.totalViewCount || 0,
                    totalViewCount: normalized.totalViewCount,
                    totalUniqueVisitors: normalized.totalUniqueVisitors,
                } as any;
                this.lastPageStatsKey = requestKey;
            });
        } catch (err: any) {
            if (this.isCancelError(err) || !this.isLatestRequest("page", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                const message = err.response?.data?.message || "Failed to fetch page stats";
                this.endpointError.page = message;
                this.error = message;
            });
        } finally {
            runInAction(() => {
                this.finishRequest("page", request.requestVersion);
            });
        }
    };

    fetchMediaStats = async (
        clientId: number | null | undefined,
        startDate: Date,
        endDate: Date,
        supplierId?: number,
        summaryOnly = false,
    ) => {
        const requestKey = `${this.buildStatsKey(clientId, startDate, endDate, supplierId)}|${summaryOnly ? "summary" : "full"}`;

        if (!summaryOnly && this.lastMediaStatsKey === requestKey && this.mediaStats) {
            return;
        }
        if (summaryOnly && this.lastMediaSummaryKey === requestKey && this.mediaStats) {
            return;
        }

        const request = this.prepareRequest("media", requestKey);
        if (!request) {
            return;
        }

        runInAction(() => {
            this.error = null;
        });

        try {
            const response = await (summaryOnly ? apiService.getMediaSummary : apiService.getMediaStats).call(
                apiService,
                startDate.toISOString().split("T")[0],
                endDate.toISOString().split("T")[0],
                clientId || undefined,
                supplierId,
                { signal: request.controller.signal },
            );

            if (!this.isLatestRequest("media", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                const normalized = this.normalizeTablePayload(response);
                this.mediaStats = {
                    data: normalized.rows,
                    total: normalized.total,
                    totalViewCount: normalized.totalViewCount,
                    totalUniqueViewers: normalized.totalUniqueViewers,
                } as any;
                if (summaryOnly) {
                    this.lastMediaSummaryKey = requestKey;
                } else {
                    this.lastMediaStatsKey = requestKey;
                }
            });
        } catch (err: any) {
            if (this.isCancelError(err) || !this.isLatestRequest("media", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                const message = err.response?.data?.message || "Failed to fetch media stats";
                this.endpointError.media = message;
                this.error = message;
            });
        } finally {
            runInAction(() => {
                this.finishRequest("media", request.requestVersion);
            });
        }
    };

    fetchDeviceStats = async (
        clientId: number | null | undefined,
        startDate: Date,
        endDate: Date,
        supplierId?: number,
    ) => {
        const requestKey = this.buildStatsKey(clientId, startDate, endDate, supplierId);
        const request = this.prepareRequest("device", requestKey);
        if (!request) {
            return;
        }

        runInAction(() => {
            this.error = null;
        });

        try {
            const response = await apiService.getDeviceStats(clientId || null, startDate, endDate, supplierId, {
                signal: request.controller.signal,
            });

            if (!this.isLatestRequest("device", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                this.deviceStats = response;
            });
        } catch (err: any) {
            if (this.isCancelError(err) || !this.isLatestRequest("device", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                const message = err.response?.data?.message || "Failed to fetch device stats";
                this.endpointError.device = message;
                this.error = message;
            });
        } finally {
            runInAction(() => {
                this.finishRequest("device", request.requestVersion);
            });
        }
    };

    fetchFlightStats = async (
        clientId: number | null | undefined,
        startDate: Date,
        endDate: Date,
        supplierId?: number,
    ) => {
        const requestKey = this.buildStatsKey(clientId, startDate, endDate, supplierId);
        const request = this.prepareRequest("flight", requestKey);
        if (!request) {
            return;
        }

        runInAction(() => {
            this.error = null;
        });

        try {
            const response = await apiService.getFlightStats(clientId || null, startDate, endDate, supplierId, {
                signal: request.controller.signal,
            });

            if (!this.isLatestRequest("flight", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                this.flightStats = response;
            });
        } catch (err: any) {
            if (this.isCancelError(err) || !this.isLatestRequest("flight", request.requestVersion)) {
                return;
            }

            runInAction(() => {
                const message = err.response?.data?.message || "Failed to fetch flight stats";
                this.endpointError.flight = message;
                this.error = message;
            });
        } finally {
            runInAction(() => {
                this.finishRequest("flight", request.requestVersion);
            });
        }
    };
}

export const statsStore = new StatsStore();
